import React, { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isArray from 'lodash/isArray';
import { isEmptyArray } from 'formik';
import Wallet from '../../components/svgIcons/Wallet.png';
import InfoIconOutline from '../../components/svgIcons/InfoIconOutline';
import StyledToolTip from '../../components/styledComponents/StyledToolTip';
import { StyledDotsLoader } from '../../../assets/styledComponents/Global';
import { useGetCompanyCreditsQuery } from '../../../features/job/jobApiSlice';
import { setHeaderCredits } from '../header/HeaderDataSlice';
import { AllowedFunc } from '../../../midleware/midleware';
import useAuth from '../../../hooks/useAuth';
import { formateNumber } from '../../../utils/helper';
import { valueToLocaleString } from '../../../features/analytics/assets/analyticsUtil';

const LOW_CREDIT_THRESHOLD = 100;

const SidebarCredits = ({ sidebarVisible }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    data: credits,
    isLoading,
    refetch: refetchOccyCredits,
  } = useGetCompanyCreditsQuery();

  useEffect(() => {
    dispatch(setHeaderCredits({ refetchOccyCredits, credits }));
  }, [credits]);

  const creditAmount = useMemo(
    () => (isArray(credits) && credits.length !== 0 ? credits[0]?.amount : 0),
    [credits],
  );

  const isLowCredits = useMemo(
    () => isEmptyArray(credits) || (credits && credits[0]?.amount < LOW_CREDIT_THRESHOLD),
    [credits],
  );

  const isCreditZero = useMemo(
    () => isEmptyArray(credits) || (credits && credits[0]?.amount < 1),
    [credits],
  );

  const isBillingAdmin = useMemo(
    () => !AllowedFunc('subcriptionRedirection', user),
    [user],
  );

  // Balance is the only value returned by the API, so the meter is scaled
  // against the app's own low-credit threshold rather than a plan total.
  const meterPercent = useMemo(() => {
    const amount = Number(creditAmount) || 0;
    if (amount <= 0) return 0;
    return Math.min(100, Math.round((amount / LOW_CREDIT_THRESHOLD) * 100));
  }, [creditAmount]);

  const isHiddenForParent =
    user?.accountType === 2 && user?.company?.isParent === 1;

  if (isHiddenForParent) return null;

  return (
    <CreditsWrap
      $visible={sidebarVisible}
      className={`cursor-pointer ${sidebarVisible ? 'mx-3 mb-3 px-3 py-2' : 'mx-2 mb-3 p-2'}`}
      onClick={() =>
        isBillingAdmin && navigate('/settings/company/subscription-hub')
      }
    >
      <StyledToolTip
        title={
          sidebarVisible
            ? isLowCredits
              ? 'Low credit balance.'
              : false
            : `${valueToLocaleString(formateNumber(creditAmount)) || 0} of ${LOW_CREDIT_THRESHOLD} remaining`
        }
        childrenClass="ms-0 w-100"
        placement="right"
      >
        {sidebarVisible ? (
          <CreditsBody>
            <div className="d-flex align-items-center gap-2 mb-2">
              <CreditsTitle>Credits</CreditsTitle>
              {isLowCredits && (
                <InfoIconOutline
                  color={theme.lowCreditLoaderColor}
                  strokeWidth={2}
                  size="16"
                />
              )}
            </div>

            <Meter>
              <MeterFill $percent={meterPercent} />
            </Meter>

            {isLoading ? (
              <LoaderWrap>
                <StyledDotsLoader
                  size={'6px'}
                  gap={'9px'}
                  color={theme?.primaryLight2}
                />
              </LoaderWrap>
            ) : (
              <CreditsMeta $isZero={isCreditZero}>
                {valueToLocaleString(formateNumber(creditAmount)) || 0} of{' '}
                {LOW_CREDIT_THRESHOLD} remaining
              </CreditsMeta>
            )}
          </CreditsBody>
        ) : (
          <CollapsedCredits>
            <img src={Wallet} alt="wallet" height={22} width={22} />
            {isLowCredits && (
              <CollapsedDot aria-hidden="true" />
            )}
          </CollapsedCredits>
        )}
      </StyledToolTip>
    </CreditsWrap>
  );
};

const CreditsWrap = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${({ theme }) => theme?.radiusLg || '16px'};
  display: flex;
  align-items: stretch;
  justify-content: ${({ $visible }) => ($visible ? 'flex-start' : 'center')};
  transition: background-color 0.2s ease;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  > span {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    display: flex;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }
`;

const CreditsBody = styled.div`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
`;

const CreditsTitle = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: ${({ theme }) => theme?.fontWeightBold || 700};
`;

const Meter = styled.div`
  width: 100%;
  height: 6px;
  border-radius: ${({ theme }) => theme?.radiusPill || '999px'};
  background-color: rgba(255, 255, 255, 0.16);
  overflow: hidden;
`;

const MeterFill = styled.div`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  border-radius: ${({ theme }) => theme?.radiusPill || '999px'};
  background-color: ${({ theme }) => theme?.secondary || '#dc601e'};
  transition: width 0.3s ease;
`;

const CreditsMeta = styled.div`
  margin-top: 8px;
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme, $isZero }) =>
    $isZero ? theme?.danger : 'rgba(255, 255, 255, 0.55)'};
`;

const LoaderWrap = styled.div`
  width: 42px;
  margin: 10px auto 2px;
`;

const CollapsedCredits = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 36px;
`;

const CollapsedDot = styled.span`
  position: absolute;
  top: 4px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme?.lowCreditLoaderColor || theme?.danger || '#ff3d00'};
`;

export default SidebarCredits;
